"use client"

import { useTheme } from "next-themes"
import { Toaster as Sonner, type ToasterProps } from "sonner"
import { SendIcon, InfoIcon, TriangleAlertIcon, OctagonXIcon, Loader2Icon } from "lucide-react"

const Toaster = ({ ...props }: ToasterProps) => {
  // Follow the resolved app theme so Sonner sets data-sonner-theme="dark" in
  // dark mode — its description text otherwise stays the light-theme dark gray
  // (#3f3f3f) on the dark `--popover` background and is barely readable.
  const { resolvedTheme } = useTheme()

  return (
    <Sonner
      theme={(resolvedTheme as ToasterProps["theme"]) ?? "system"}
      closeButton
      // Lift toasts above the shell's bottom tab bar. Sonner's default
      // bottom offset is ~24px; the native bar is ~83px AND is a UIKit view
      // ABOVE the webview, so a toast at the default offset is not merely
      // overlapped — it is unreachable, with no z-index that can rescue it.
      // `--mobile-tabs-h` is absent outside the shell, so this collapses to
      // the plain 1rem gutter on web and on immersive routes.
      //
      // BOTH props are required. Sonner scopes `mobileOffset` to its own
      // `@media (max-width: 600px)` block and falls back to `offset` above
      // that — and both configurations we actually ship the tab bar on are
      // above it: iPhone landscape (844-956 CSS px, enabled in Info.plist)
      // and iPad (820-1366, universal target, no viewport gate on the bar).
      // Setting only the mobile one left every toast under the bar there.
      offset={{ bottom: "calc(1rem + var(--mobile-tabs-h))" }}
      mobileOffset={{ bottom: "calc(1rem + var(--mobile-tabs-h))" }}
      className="toaster group"
      icons={{
        success: (
          <SendIcon className="size-4" />
        ),
        info: (
          <InfoIcon className="size-4" />
        ),
        warning: (
          <TriangleAlertIcon className="size-4" />
        ),
        error: (
          <OctagonXIcon className="size-4" />
        ),
        loading: (
          <Loader2Icon className="size-4 animate-spin" />
        ),
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: "cn-toast",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
